import {Injectable} from '@angular/core';
import {Landlord} from '@landlords/models';
import {LandlordsService} from '@landlords/services/landlords.service';
import type {PropertyUnitPeriod} from '@periods/models/property.unit.period.interface';
import {PeriodsService} from '@periods/services/periods.service';
import type {Property} from '@properties/models/property.interface';
import {PropertiesService} from '@properties/services/properties.service';
import {isTaxableCategory, resolveVatCategory} from '@vat/engine/resolve-vat-category';
import firebase from 'firebase/compat/app';
import {defer, firstValueFrom, from, Observable} from 'rxjs';
import {
    buildLandingRow,
    buildSuggestionRows,
    groupReturnsByLandlord,
    partitionDismissedSuggestions,
    sortLandingRows,
    VatLandingDismissedSuggestion,
    VatLandingRow,
    VatLandingSuggestion,
    VatLandingSuggestionPeriod
} from '../components/landing/vat-landing.util';
import {toPeriodLite} from './vat-period-lite.adapter';
import {VatReturnsService} from './vat-returns.service';

export interface VatLandingData {
    /** Eingerichtete Eigentümer (taxProfile.setupCompletedAt gesetzt), sortiert nach Anzeigename. */
    rows: VatLandingRow[];
    /**
     * Aktive Vorschläge: Eigentümer ohne Steuerprofil mit aktuellen gewerblichen Mietverhältnissen, die Umsatzsteuer
     * ausweisen (B1, `isTaxableSuggestionPeriod`), ohne die für das Team ausgeblendeten; commercialCount desc, dann Name.
     */
    suggestions: VatLandingSuggestion[];
    /** Für das Team ausgeblendete Vorschläge (`taxProfile.suggestionDismissed`, B2) – gleiche Sortierung wie `suggestions`. */
    dismissedSuggestions: VatLandingDismissedSuggestion[];
    /**
     * UNTERGRENZE der Eigentümer des Kunden – ausschließlich für die Weiche „gar keine Eigentümer“ (`> 0`, Spec 1.3),
     * nie als Zähler anzeigen: Zeilen + Vorschläge (aktiv und ausgeblendet) ohne Zusatzquery; liegen weder Zeilen noch
     * Vorschläge vor, Existenzprüfung `landlords` mit `limit(1)` (0 oder 1, ein Dokument-Read statt Vollscan – Review C6).
     */
    totalLandlords: number;
}

/**
 * B1 (Mock-Freigabe 13.09.): Ein gewerbliches Mietverhältnis begründet nur dann einen Vorschlag, wenn es tatsächlich
 * Umsatzsteuer ausweist – Kategorie-Regel des Rechenkerns (`STEUERPFLICHTIG_19`/`_7`). Alt-Perioden ohne `vatCategory`
 * folgen der Ableitung in `resolveVatCategory` (Gewerbe ohne Satz → 19 %; 20 %/0 % → SONSTIGER_SATZ → kein Vorschlag).
 */
export const isTaxableSuggestionPeriod = (period: PropertyUnitPeriod): boolean =>
    isTaxableCategory(resolveVatCategory(toPeriodLite(period)).category);

/** Firestore-`in` erlaubt bis zu 30 Werte; Spec 1.2 begrenzt Eigentümer-Nachschläge bewusst auf 10er-Chunks. */
export const VAT_LANDING_IN_CHUNK_SIZE = 10;
/** Spec 1.2: Fallback über properties/{id} nur für maximal 25 Immobilien ohne `landlordID` an der Periode. */
export const VAT_LANDING_MAX_PROPERTY_LOOKUPS = 25;

const chunk = <T>(items: T[], size: number): T[][] => {
    const result: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        result.push(items.slice(i, i + size));
    }
    return result;
};

/**
 * Landing v2 (Spec 2026-09-13 Sektion 1.2): 2 + 1 Queries statt 1 + N – eingerichtete Eigentümer (Range auf
 * `taxProfile.setupCompletedAt`), alle vatReturns des Jahres UND des Vorjahres (`listByYear` je Jahr, zwei
 * Gleichheits-Queries – der letzte Vorjahres-Zeitraum ist erst im Januar/Februar fällig, Review C5) und die gewerblichen
 * aktuellen Perioden des Root-Spiegels (`isCommercial == true && isCurrent == true`, Gleichheitsfilter, kein
 * Composite-Index). Route-provided (app.routing.ts, wie die Fassade); `load()` ist ein Einmal-Snapshot, Reload über den
 * Landing-Retry. Fehler jeder Teilabfrage → Observable-Fehler (kein stiller Teilzustand).
 *
 * @author Anthony Nahas
 * @since 13.09.26
 */
@Injectable()
export class VatLandingDataService {

    constructor(
        private landlordsService: LandlordsService,
        private vatReturnsService: VatReturnsService,
        private periodsService: PeriodsService,
        private propertiesService: PropertiesService,
    ) {
    }

    load(now: Date): Observable<VatLandingData> {
        return defer(() => from(this.loadSnapshot(now)));
    }

    private async loadSnapshot(now: Date): Promise<VatLandingData> {
        this.periodsService.resetPath();   // Root-Spiegel `periods` (kein parentPath), nur lesen
        const year = now.getFullYear();
        const [configuredSnapshot, returnsOfYear, returnsOfPreviousYear, periodsSnapshot] = await Promise.all([
            firstValueFrom(this.landlordsService.collection(ref => ref.where('taxProfile.setupCompletedAt', '>', new Date(0))).get()),
            firstValueFrom(this.vatReturnsService.listByYear(year)),
            // Vorjahr für den Jahreswechsel-Überhang (M12/Q4 fällig 10.01./10.02.), siehe buildPreviousYearCarryOverCards
            firstValueFrom(this.vatReturnsService.listByYear(year - 1)),
            firstValueFrom(this.periodsService.collection(ref => ref.where('isCommercial', '==', true).where('isCurrent', '==', true)).get()),
        ]);

        const configured = configuredSnapshot.docs.map(doc => ({...(doc.data() as Landlord), id: doc.id}));
        const configuredIDs = new Set(configured.map(landlord => landlord.id));
        const grouped = groupReturnsByLandlord([...returnsOfYear, ...returnsOfPreviousYear]);
        const rows = sortLandingRows(configured.map(landlord => buildLandingRow(landlord, grouped.get(landlord.id) ?? [], now)));

        // B1: nur Mietverhältnisse mit Umsatzsteuer – VOR dem Fallback-Lookup, damit steuerfreie keine Nachschläge kosten
        const taxablePeriods = periodsSnapshot.docs.map(doc => doc.data() as PropertyUnitPeriod).filter(isTaxableSuggestionPeriod);
        const periods = await this.resolveLandlordIDs(taxablePeriods);
        const candidateIDs = [...new Set(periods.map(period => period.landlordID).filter((id): id is string => !!id && !configuredIDs.has(id)))];
        const landlordsByID = await this.loadLandlordsByID(candidateIDs);
        // B2: für das Team ausgeblendete Eigentümer (taxProfile.suggestionDismissed) als leise Rest-Zeile abtrennen
        const {suggestions, dismissedSuggestions} = partitionDismissedSuggestions(buildSuggestionRows(periods, landlordsByID, configuredIDs));

        // Untergrenze für die Weiche „gar keine Eigentümer“; nur ohne Zeilen UND Vorschläge eine limit(1)-Existenzprüfung
        const known = rows.length + suggestions.length + dismissedSuggestions.length;
        const totalLandlords = known > 0
            ? known
            : (await firstValueFrom(this.landlordsService.collection(ref => ref.limit(1)).get())).size;

        return {rows, suggestions, dismissedSuggestions, totalLandlords};
    }

    /** Perioden ohne `landlordID`: Fallback `properties/{propertyID}.landlord.id`, je Immobilie einmal, max. 25 Nachschläge. */
    private async resolveLandlordIDs(periods: PropertyUnitPeriod[]): Promise<VatLandingSuggestionPeriod[]> {
        const missingPropertyIDs = [...new Set(periods.filter(period => !period.landlordID && period.propertyID).map(period => period.propertyID))]
            .slice(0, VAT_LANDING_MAX_PROPERTY_LOOKUPS);
        if (!missingPropertyIDs.length) {
            return periods;
        }
        const resolved = new Map<string, string | null>();
        await Promise.all(missingPropertyIDs.map(async propertyID => {
            const snapshot = await firstValueFrom(this.propertiesService.doc(propertyID).get());
            const property = snapshot.exists ? snapshot.data() as Property : null;
            resolved.set(propertyID, property?.landlord?.id ?? null);
        }));
        return periods.map(period => period.landlordID || !period.propertyID
            ? period
            : {...period, landlordID: resolved.get(period.propertyID) ?? undefined});
    }

    /** Eigentümer-Dokumente der Vorschläge in 10er-Chunks über `documentId() in`. */
    private async loadLandlordsByID(ids: string[]): Promise<Map<string, Landlord>> {
        const byID = new Map<string, Landlord>();
        if (!ids.length) {
            return byID;
        }
        const documentID = firebase.firestore.FieldPath.documentId();
        const snapshots = await Promise.all(chunk(ids, VAT_LANDING_IN_CHUNK_SIZE).map(ids10 =>
            firstValueFrom(this.landlordsService.collection(ref => ref.where(documentID, 'in', ids10)).get())));
        snapshots.forEach(snapshot => snapshot.docs.forEach(doc => byID.set(doc.id, {...(doc.data() as Landlord), id: doc.id})));
        return byID;
    }
}
